import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAssetProblemReport } from '../assetProblemReportService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';

const marketKey = (stateCode, marketId) => `${stateCode}::${marketId}`;

const toggleSet = (set, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
};

const addToSet = (set, keys) => {
    const next = new Set(set);
    keys.forEach((k) => next.add(k));
    return next;
};

// Drives the independent State → Market → Team drilldown accordion shown below the
// summary/charts. Each level is fetched lazily — and cached — the first time a node is
// expanded, so re-collapsing/expanding a node never refetches it. Expanding a state eagerly
// loads (and auto-opens) its markets, and that in turn eagerly loads each market's teams, so
// a market/team never renders "open" with no data until manually closed and reopened.
//
// `filterScope` (selected states/markets/teams + the filters reference lists) drives two
// extra behaviors: matching branches auto-expand/load, and non-matching branches are hidden
// via the `isMarketVisible`/`isTeamVisible`/`visibleStateKeys` helpers.
export function useBreakdownAccordion(startDate, endDate, filterScope = {}) {
    const { selectedStates = [], selectedMarkets = [], selectedTeamIds = [], filters = {} } = filterScope;

    const [stateGroups, setStateGroups] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [openStates, setOpenStates] = useState(new Set());
    const [openMarkets, setOpenMarkets] = useState(new Set());
    const [expandedTeams, setExpandedTeams] = useState(new Set());
    const [marketsByState, setMarketsByState] = useState({});
    const [teamsByMarket, setTeamsByMarket] = useState({});
    const [loadingKeys, setLoadingKeys] = useState(new Set());
    const loadingRef = useRef(new Set());

    const dateParams = useCallback(() => {
        const params = {};
        if (startDate) params.start = formatDateToYyyyMmDd(startDate);
        if (endDate) params.end = formatDateToYyyyMmDd(endDate);
        return params;
    }, [startDate, endDate]);

    // Date range changes invalidate every cached child node.
    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setError('');
        setMarketsByState({});
        setTeamsByMarket({});
        setOpenStates(new Set());
        setOpenMarkets(new Set());
        setExpandedTeams(new Set());
        loadingRef.current = new Set();
        getAssetProblemReport({ level: 'state', ...dateParams() })
            .then((result) => { if (!cancelled) setStateGroups(result?.groups ?? []); })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [dateParams]);

    const loadTeamsForMarket = useCallback((stateCode, marketId) => {
        const key = marketKey(stateCode, marketId);
        if (teamsByMarket[key] || loadingRef.current.has(key)) return;
        loadingRef.current.add(key);
        setLoadingKeys((keys) => new Set(keys).add(key));
        getAssetProblemReport({ level: 'team', state: stateCode, market_id: marketId, ...dateParams() })
            .then((result) => setTeamsByMarket((cur) => ({ ...cur, [key]: result?.groups ?? [] })))
            .catch((err) => setError(err.message))
            .finally(() => {
                loadingRef.current.delete(key);
                setLoadingKeys((keys) => { const next = new Set(keys); next.delete(key); return next; });
            });
    }, [teamsByMarket, dateParams]);

    const loadMarketsForState = useCallback((stateCode) => {
        if (marketsByState[stateCode] || loadingRef.current.has(stateCode)) return;
        loadingRef.current.add(stateCode);
        setLoadingKeys((keys) => new Set(keys).add(stateCode));
        getAssetProblemReport({ level: 'market', state: stateCode, ...dateParams() })
            .then((result) => {
                const markets = result?.groups ?? [];
                setMarketsByState((cur) => ({ ...cur, [stateCode]: markets }));
                // Auto-expand every market under this state and eagerly load its teams, so
                // opening a state never leaves a market looking "open" with no data yet.
                setOpenMarkets((prev) => addToSet(prev, markets.map((m) => marketKey(stateCode, m.key))));
                markets.forEach((m) => loadTeamsForMarket(stateCode, m.key));
            })
            .catch((err) => setError(err.message))
            .finally(() => {
                loadingRef.current.delete(stateCode);
                setLoadingKeys((keys) => { const next = new Set(keys); next.delete(stateCode); return next; });
            });
    }, [marketsByState, dateParams, loadTeamsForMarket]);

    const toggleState = useCallback((stateCode) => {
        setOpenStates((prev) => toggleSet(prev, stateCode));
        loadMarketsForState(stateCode);
    }, [loadMarketsForState]);

    const toggleMarket = useCallback((stateCode, marketId) => {
        const key = marketKey(stateCode, marketId);
        setOpenMarkets((prev) => toggleSet(prev, key));
        loadTeamsForMarket(stateCode, marketId);
    }, [loadTeamsForMarket]);

    // Purely local UI state — expanding a team's inline problems panel never touches the
    // Teams filter/URL.
    const toggleTeamExpanded = useCallback((teamKey) => {
        setExpandedTeams((prev) => toggleSet(prev, teamKey));
    }, []);

    // ─── Filter-driven auto expand/segment ───────────────────────────────────
    // State/Market/Team filters all narrow and auto-expand this accordion down to the
    // matching branches. The Teams filter additionally scopes the main dashboard summary
    // request (via `team_id`, wired in useAssetProblemReport) — that part lives outside
    // this hook.

    const marketStateById = useMemo(() => {
        const map = new Map();
        (filters.markets || []).forEach((m) => map.set(String(m.id), m.state));
        return map;
    }, [filters.markets]);

    // Teams carry their own market/state, resolved from the filters' global team list — this
    // is what lets selecting a team (with no state/market explicitly chosen) still resolve
    // and auto-load the right branch of the tree.
    const teamScopeById = useMemo(() => {
        const map = new Map();
        (filters.teams || []).forEach((t) => map.set(String(t.id), { marketId: t.marketId, state: t.state }));
        return map;
    }, [filters.teams]);

    const hasActiveFilters = selectedStates.length > 0 || selectedMarkets.length > 0 || selectedTeamIds.length > 0;

    // Null means "no filter" (everything visible); a Set means "only these are in scope".
    const effectiveStates = useMemo(() => {
        if (!hasActiveFilters) return null;
        const set = new Set(selectedStates);
        selectedMarkets.forEach((id) => {
            const state = marketStateById.get(String(id));
            if (state) set.add(state);
        });
        selectedTeamIds.forEach((id) => {
            const scope = teamScopeById.get(String(id));
            if (scope?.state) set.add(scope.state);
        });
        return set;
    }, [hasActiveFilters, selectedStates, selectedMarkets, selectedTeamIds, marketStateById, teamScopeById]);

    const effectiveMarkets = useMemo(() => {
        if (!hasActiveFilters) return null;
        const set = new Set(selectedMarkets.map(String));
        selectedTeamIds.forEach((id) => {
            const scope = teamScopeById.get(String(id));
            if (scope?.marketId != null) set.add(String(scope.marketId));
        });
        return set;
    }, [hasActiveFilters, selectedMarkets, selectedTeamIds, teamScopeById]);

    const effectiveTeams = useMemo(
        () => (hasActiveFilters ? new Set(selectedTeamIds.map(String)) : null),
        [hasActiveFilters, selectedTeamIds],
    );

    // Expand + load every state/market that matches the current filter scope.
    useEffect(() => {
        if (!effectiveStates || stateGroups.length === 0) return;
        setOpenStates((prev) => addToSet(prev, Array.from(effectiveStates)));
        effectiveStates.forEach((code) => loadMarketsForState(code));
        // Only re-run when the filter scope (or the root state list) actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveStates, stateGroups]);

    // Once a filtered market's teams load, auto-expand its team panels: an explicitly
    // selected market means "show me everything under this market", so every team there opens
    // already. A team filter (without that market being explicitly selected) only opens the
    // matching team(s), not its siblings.
    useEffect(() => {
        if (!hasActiveFilters) return;
        const selectedMarketKeys = new Set(selectedMarkets.map(String));
        const keysToExpand = [];
        Object.entries(teamsByMarket).forEach(([key, teams]) => {
            const marketId = key.split('::')[1];
            if (selectedMarketKeys.has(String(marketId))) {
                teams.forEach((t) => keysToExpand.push(t.key));
                return;
            }
            if (effectiveTeams && effectiveTeams.size > 0) {
                teams.forEach((t) => { if (effectiveTeams.has(String(t.key))) keysToExpand.push(t.key); });
            }
        });
        if (keysToExpand.length > 0) {
            setExpandedTeams((prev) => addToSet(prev, keysToExpand));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamsByMarket, effectiveTeams, selectedMarkets, hasActiveFilters]);

    // ─── Visibility (hide non-matching branches once a filter is active) ────

    const visibleStateKeys = effectiveStates;

    const isMarketVisible = useCallback((stateCode, marketId) => {
        if (!hasActiveFilters) return true;
        if (effectiveStates && !effectiveStates.has(stateCode)) return false;
        if (selectedMarkets.length === 0 && selectedTeamIds.length === 0) return true;
        return effectiveMarkets ? effectiveMarkets.has(String(marketId)) : true;
    }, [hasActiveFilters, effectiveStates, effectiveMarkets, selectedMarkets, selectedTeamIds]);

    // A team filter additionally narrows the market's team list down to just the selected team(s).
    const isTeamVisible = useCallback((stateCode, marketId, teamId) => {
        if (!hasActiveFilters) return true;
        if (!isMarketVisible(stateCode, marketId)) return false;
        if (selectedTeamIds.length === 0) return true;
        return effectiveTeams ? effectiveTeams.has(String(teamId)) : true;
    }, [hasActiveFilters, isMarketVisible, effectiveTeams, selectedTeamIds]);

    return {
        stateGroups,
        loaded,
        error,
        openStates,
        openMarkets,
        expandedTeams,
        marketsByState,
        teamsByMarket,
        loadingKeys,
        toggleState,
        toggleMarket,
        toggleTeamExpanded,
        marketKey,
        visibleStateKeys,
        isMarketVisible,
        isTeamVisible,
    };
}

